import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useCaeControlUniverse } from '../../../hooks/analytics/useCaeReports.ts';
import { RefreshMeta, StatusChip } from '../controller/shared.tsx';
import type { CAEControlUniverseRow } from '../../../request/analytics/caeReports.ts';

/**
 * Report #9 — Control Universe Inventory. Full sortable/searchable list of
 * every control in the org's universe, one row per control.
 */
export const ControlUniverseTable: React.FC<{ rows?: CAEControlUniverseRow[] }> = ({ rows: rowsOverride }) => {
  const { data: fetched, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCaeControlUniverse();
  const data = rowsOverride ?? fetched;

  const columns = useMemo<TableColumnDef<CAEControlUniverseRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.control_id}</span>,
        widthPercent: 10,
      },
      {
        id: 'title',
        columnLabel: 'Title',
        header: () => 'Title',
        cell: ({ row }) => row.original.title,
        widthPercent: 22,
      },
      {
        id: 'control_type',
        columnLabel: 'Type',
        header: () => 'Type',
        cell: ({ row }) => <span className="capitalize">{row.original.control_type}</span>,
        widthPercent: 10,
      },
      {
        id: 'frequency',
        columnLabel: 'Frequency',
        header: () => 'Frequency',
        cell: ({ row }) => <span className="capitalize">{row.original.frequency ?? '—'}</span>,
        widthPercent: 10,
      },
      {
        id: 'status',
        columnLabel: 'Status',
        header: () => 'Status',
        cell: ({ row }) => <StatusChip value={row.original.status} />,
        widthPercent: 10,
      },
      {
        id: 'significance',
        columnLabel: 'Significance',
        header: () => 'Significance',
        cell: ({ row }) => <span className="capitalize">{row.original.significance ?? '—'}</span>,
        widthPercent: 10,
      },
      {
        id: 'department',
        columnLabel: 'Dept',
        header: () => 'Dept',
        cell: ({ row }) => row.original.department ?? '—',
        widthPercent: 12,
      },
      {
        id: 'classification',
        columnLabel: 'Classification',
        header: () => 'Classification',
        cell: ({ row }) => <span className="capitalize">{row.original.classification ?? '—'}</span>,
        widthPercent: 10,
      },
      {
        id: 'flags',
        columnLabel: 'Flags',
        header: () => 'Flags',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.mrc_control && (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-purple-700">
                MRC
              </span>
            )}
            {row.original.external_auditor_reliance && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
                Reliance
              </span>
            )}
            {row.original.itgc_domain && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-700">
                {row.original.itgc_domain}
              </span>
            )}
          </div>
        ),
        widthPercent: 16,
      },
    ],
    []
  );

  return (
    <AppDataTable<CAEControlUniverseRow>
      data={data ?? []}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load control universe.' : null}
      emptyMessage="No controls found."
      searchable
      searchPlaceholder="Search controls..."
      paginated
      defaultPageSize={10}
      showPageSizeSelector
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default ControlUniverseTable;
